import React, { useState, useMemo } from 'react';
import { Translate } from 'react-localize-redux';
import { InsuranceBill, InsuranceBillClaim, DocumentStatus, CreatePaymentRequest, PaymentRecord } from './types';
import { Box, Button, Card, Chip, Grid, Typography, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { formatCurrency, fullDateFromPostgresString } from '../../../utils';
import { documentStatusToIcon } from '.';
import { documentStatusToColor, documentTypeToColor } from '../../../utils/bill';
import { ColourButton } from '../../../components/general/mini_components-ts';
import { ColourChip } from '../../../components/general/mini_components-ts';
import { EnhancedTable } from '../../../components/general/EnhancedTable';
import { useInsuranceBill } from '../../../hooks/useInsuranceBill';
import { closeInsuranceBillService, updateBillItemsStatusService } from '../../../services/billing';
import { useSnackBarState } from '../../../hooks';
import Loader from '../../../components/Loader';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PaymentIcon from '@mui/icons-material/Payment';
import CloseIcon from '@mui/icons-material/Close';
import ListIcon from '@mui/icons-material/List';

interface InsuranceBillViewProps {
    investigationUuid: string;
    billUuid: string;
    currency: string;
    languageCode: string;
    onClose?: () => void;
    onBillUpdated?: () => void;
    // For Storybook/testing - bypass hook if provided
    bill?: InsuranceBill | null;
    claims?: InsuranceBillClaim[];
    loading?: boolean;
    error?: string | null;
}

const InsuranceBillView: React.FC<InsuranceBillViewProps> = ({
    investigationUuid,
    billUuid,
    currency,
    languageCode,
    onClose,
    onBillUpdated,
    bill: providedBill,
    claims: providedClaims,
    loading: providedLoading,
    error: providedError
}) => {
    // Use provided data for Storybook/testing, otherwise use hook
    const hookResult = useInsuranceBill({
        investigationUuid,
        billUuid,
        enabled: !providedBill // Disable hook if bill is provided
    });

    const bill = providedBill !== undefined ? providedBill : hookResult.bill;
    const claims = providedClaims || hookResult.claims;
    const loading = providedLoading !== undefined ? providedLoading : hookResult.loading;
    const error = providedError !== undefined ? providedError : hookResult.error;
    const refetch = hookResult.refetch;

    const [showCloseConfirm, setShowCloseConfirm] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showClaimsModal, setShowClaimsModal] = useState(false);
    const [closing, setClosing] = useState(false);
    const [showSnackbar, setShowSnackbar] = useSnackBarState();

    const remainingBalance = useMemo(() => {
        if (!bill) return 0;
        return bill.totalGross - bill.totalPaid;
    }, [bill]);

    const claimsCount = useMemo(() => {
        return claims.length;
    }, [claims]);

    const handleCloseBill = async () => {
        if (!bill) return;

        setClosing(true);
        try {
            await closeInsuranceBillService(investigationUuid, bill.uuid);
            setShowSnackbar({
                show: true,
                message: 'Bill closed successfully',
                severity: 'success'
            });
            setShowCloseConfirm(false);
            refetch();
            if (onBillUpdated) {
                onBillUpdated();
            }
        } catch (err) {
            setShowSnackbar({
                show: true,
                message: err instanceof Error ? err.message : 'Failed to close bill',
                severity: 'error'
            });
        } finally {
            setClosing(false);
        }
    };

    const handleRecordPayment = async (payment: CreatePaymentRequest) => {
        if (!bill) return;

        try {
            // Use existing payment endpoint - create minimal bill object for payment recording
            const minimalBill = {
                ...bill,
                billItems: bill.billItems || []
            } as any; // Type assertion needed as InsuranceBill doesn't match Bill type exactly
            await updateBillItemsStatusService(investigationUuid, bill.uuid, minimalBill, payment);
            setShowSnackbar({
                show: true,
                message: 'Payment recorded successfully',
                severity: 'success'
            });
            setShowPaymentModal(false);
            refetch();
            if (onBillUpdated) {
                onBillUpdated();
            }
        } catch (err) {
            setShowSnackbar({
                show: true,
                message: err instanceof Error ? err.message : 'Failed to record payment',
                severity: 'error'
            });
        }
    };

    if (loading) {
        return <Loader />;
    }

    if (error || !bill) {
        return (
            <Card style={{ padding: '2rem', textAlign: 'center' }}>
                <Typography color="error">{error || 'Bill not found'}</Typography>
                {onClose && (
                    <Button onClick={onClose} style={{ marginTop: '1rem' }}>
                        <Translate id="general.back" />
                    </Button>
                )}
            </Card>
        );
    }

    const canClose = bill.status === DocumentStatus.DRAFT;
    const canRecordPayment = bill.status === DocumentStatus.CLOSED && remainingBalance > 0;

    // Claims table rows
    const claimsRows = claims.map((claim, index) => ({
        id: index,
        uuid: claim.uuid,
        guaranteeLetter: claim.guaranteeLetterNumber,
        requestedAmount: formatCurrency(claim.requestedAmount, currency, languageCode),
        approvedAmount: claim.approvedAmount
            ? formatCurrency(claim.approvedAmount, currency, languageCode)
            : '-',
        status: claim.status,
        interventionDate: claim.interventionDate
            ? fullDateFromPostgresString(languageCode, claim.interventionDate)
            : '-',
        patientBill: claim.bill?.billIdInvestigation?.toString() || '-'
    }));

    // Payment records rows
    const paymentRows = (bill.paymentRecords || []).map((payment, index) => ({
        id: index,
        uuid: payment.id.toString(),
        amount: formatCurrency(payment.amount, currency, languageCode),
        date: payment.createdAt
            ? fullDateFromPostgresString(languageCode, payment.createdAt)
            : '-',
        note: payment.paymentData?.note || '-'
    }));

    return (
        <Grid container spacing={2}>
            {/* Header with Organization Info */}
            <Grid item xs={12}>
                <Card>
                    <Box padding={2}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <Typography variant="h5" component="h2">
                                    {bill.organizationName}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    <Translate id="hospital.billing.insurance_bills.insurance_bill" />
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6} style={{ textAlign: 'right' }}>
                                <ColourButton
                                    icon={documentStatusToIcon(bill.status)}
                                    rgbcolor={documentStatusToColor(bill.status)}
                                    label={
                                        <Translate
                                            id={`hospital.billing.bill.status.${bill.status === DocumentStatus.DRAFT ? 'draft' : 'closed'}`}
                                        />
                                    }
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Card>
            </Grid>

            {/* Summary Card */}
            <Grid item xs={12} md={4}>
                <Card>
                    <Box padding={2}>
                        <Typography variant="h6" gutterBottom>
                            <Translate id="hospital.billing.bill.summary" />
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="textSecondary">
                                    <Translate id="hospital.billing.bill.total" />
                                </Typography>
                                <Typography variant="h6">
                                    {formatCurrency(bill.totalGross, currency, languageCode)}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="textSecondary">
                                    <Translate id="hospital.billing.bill.paid" />
                                </Typography>
                                <Typography variant="h6" color="success.main">
                                    {formatCurrency(bill.totalPaid, currency, languageCode)}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="textSecondary">
                                    <Translate id="hospital.billing.bill.remaining" />
                                </Typography>
                                <Typography variant="h6" color={remainingBalance > 0 ? 'error' : 'success.main'}>
                                    {formatCurrency(remainingBalance, currency, languageCode)}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="textSecondary">
                                    <Translate id="hospital.billing.insurance_bills.claims" />
                                </Typography>
                                <Typography variant="h6">{claimsCount}</Typography>
                            </Grid>
                        </Grid>
                    </Box>
                </Card>
            </Grid>

            {/* Actions Card */}
            <Grid item xs={12} md={8}>
                <Card>
                    <Box padding={2}>
                        <Typography variant="h6" gutterBottom>
                            <Translate id="hospital.billing.bill.actions" />
                        </Typography>
                        <Grid container spacing={2}>
                            {canClose && (
                                <Grid item>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<CloseIcon />}
                                        onClick={() => setShowCloseConfirm(true)}
                                    >
                                        <Translate id="hospital.billing.insurance_bills.close_bill" />
                                    </Button>
                                </Grid>
                            )}
                            {canRecordPayment && (
                                <Grid item>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        startIcon={<PaymentIcon />}
                                        onClick={() => setShowPaymentModal(true)}
                                    >
                                        <Translate id="hospital.billing.bill.record_payment" />
                                    </Button>
                                </Grid>
                            )}
                            <Grid item>
                                <Button
                                    variant="outlined"
                                    startIcon={<ListIcon />}
                                    onClick={() => setShowClaimsModal(true)}
                                >
                                    <Translate id="hospital.billing.insurance_bills.view_claims" /> ({claimsCount})
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button
                                    variant="outlined"
                                    startIcon={<PictureAsPdfIcon />}
                                    onClick={() => {
                                        // Handle PDF generation
                                        console.log('Generate PDF for bill:', bill.uuid);
                                    }}
                                >
                                    <Translate id="hospital.billing.bill.view_pdf" />
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Card>
            </Grid>

            {/* Payment Records */}
            {paymentRows.length > 0 && (
                <Grid item xs={12}>
                    <Card>
                        <Box padding={2}>
                            <Typography variant="h6" gutterBottom>
                                <Translate id="hospital.billing.bill.payment_records" />
                            </Typography>
                            <EnhancedTable
                                noHeader
                                headCells={[
                                    {
                                        id: 'date',
                                        alignment: 'left' as const,
                                        label: <Translate id="hospital.billing.bill.date" />
                                    },
                                    {
                                        id: 'amount',
                                        alignment: 'left' as const,
                                        label: <Translate id="hospital.billing.bill.amount" />
                                    },
                                    {
                                        id: 'note',
                                        alignment: 'left' as const,
                                        label: <Translate id="hospital.billing.bill.note" />
                                    }
                                ]}
                                rows={paymentRows}
                                noSelectable
                            />
                        </Box>
                    </Card>
                </Grid>
            )}

            {/* Close Bill Confirmation Modal */}
            <Dialog open={showCloseConfirm} onClose={() => setShowCloseConfirm(false)}>
                <DialogTitle>
                    <Translate id="hospital.billing.insurance_bills.close_bill_confirm_title" />
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        <Translate id="hospital.billing.insurance_bills.close_bill_confirm_message" />
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowCloseConfirm(false)} disabled={closing}>
                        <Translate id="general.cancel" />
                    </Button>
                    <Button onClick={handleCloseBill} variant="contained" color="primary" disabled={closing}>
                        {closing ? <Translate id="general.loading" /> : <Translate id="general.confirm" />}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Claims Modal */}
            <Dialog open={showClaimsModal} onClose={() => setShowClaimsModal(false)} maxWidth="lg" fullWidth>
                <DialogTitle>
                    <Translate id="hospital.billing.insurance_bills.claims" /> ({claimsCount})
                </DialogTitle>
                <DialogContent>
                    {claimsRows.length > 0 ? (
                        <EnhancedTable
                            noHeader
                            headCells={[
                                {
                                    id: 'guaranteeLetter',
                                    alignment: 'left' as const,
                                    label: <Translate id="hospital.billing.insurance_bills.guarantee_letter" />
                                },
                                {
                                    id: 'requestedAmount',
                                    alignment: 'left' as const,
                                    label: <Translate id="hospital.billing.insurance_bills.requested_amount" />
                                },
                                {
                                    id: 'approvedAmount',
                                    alignment: 'left' as const,
                                    label: <Translate id="hospital.billing.insurance_bills.approved_amount" />
                                },
                                {
                                    id: 'status',
                                    alignment: 'left' as const,
                                    label: <Translate id="hospital.billing.bill.status.label" />
                                },
                                {
                                    id: 'interventionDate',
                                    alignment: 'left' as const,
                                    label: <Translate id="hospital.billing.insurance_bills.intervention_date" />
                                },
                                {
                                    id: 'patientBill',
                                    alignment: 'left' as const,
                                    label: <Translate id="hospital.billing.insurance_bills.patient_bill" />
                                }
                            ]}
                            rows={claimsRows}
                            noSelectable
                        />
                    ) : (
                        <Typography>
                            <Translate id="hospital.billing.insurance_bills.no_claims" />
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowClaimsModal(false)}>
                        <Translate id="general.close" />
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Payment Modal - Would need to be implemented with payment form */}
            <Dialog open={showPaymentModal} onClose={() => setShowPaymentModal(false)}>
                <DialogTitle>
                    <Translate id="hospital.billing.bill.record_payment" />
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        <Translate id="hospital.billing.insurance_bills.payment_form_placeholder" />
                    </Typography>
                    {/* Payment form would go here - reuse from BillView */}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowPaymentModal(false)}>
                        <Translate id="general.cancel" />
                    </Button>
                    <Button variant="contained" color="primary">
                        <Translate id="general.save" />
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

export default InsuranceBillView;

